import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  isAdmin: false,
  isLoading: true,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const checkAdminRole = async (userId: string) => {
      try {
        const { data, error } = await supabase.rpc('has_role', {
          _user_id: userId,
          _role: 'admin'
        });
        
        if (!error && mounted) {
          setIsAdmin(data === true);
        }
      } catch (error) {
        console.error('[AuthContext] Error checking admin role:', error);
      }
    };

    const handleSession = async (newSession: Session | null) => {
      if (!mounted) return;
      
      console.log('[AuthContext] handleSession called:', {
        timestamp: new Date().toISOString(),
        hasSession: !!newSession,
        userId: newSession?.user?.id,
        email: newSession?.user?.email,
      });
      
      setSession(newSession);
      
      if (newSession?.user) {
        // Wait for admin check to complete before setting loading to false
        await checkAdminRole(newSession.user.id);
      } else {
        setIsAdmin(false);
      }
      
      if (mounted) {
        console.log('[AuthContext] Setting isLoading to false');
        setIsLoading(false);
      }
    };

    // Set up auth state listener FIRST - capture all events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('[AuthContext] onAuthStateChange fired:', {
          timestamp: new Date().toISOString(),
          event,
          hasSession: !!newSession,
          email: newSession?.user?.email,
        });
        
        if (!mounted) return;
        
        // Use setTimeout to avoid Supabase deadlock
        setTimeout(() => {
          if (mounted) {
            handleSession(newSession);
          }
        }, 0);
      }
    );

    // Then get initial session
    console.log('[AuthContext] Calling getSession...', { timestamp: new Date().toISOString() });
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      console.log('[AuthContext] getSession result:', {
        timestamp: new Date().toISOString(),
        hasSession: !!existingSession,
        email: existingSession?.user?.email,
      });
      if (!mounted) return;
      handleSession(existingSession);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const value = useMemo(() => ({
    session,
    user: session?.user ?? null,
    isAdmin,
    isLoading,
  }), [session, isAdmin, isLoading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
